import { defineStore } from 'pinia'
import { fetchRestaurantCategoriesApi } from '@/entities/restaurant/api/restaurantCategoriesApi'
import {
  deleteRestaurantProductApi,
  fetchRestaurantProductsApi,
  RESTAURANT_PRODUCTS_PAGE_SIZE,
  UNCATEGORIZED_CATEGORY_ID,
  updateRestaurantProductApi,
} from '@/entities/restaurant/api/restaurantProductsApi'

const UNCATEGORIZED_TAB = {
  id: UNCATEGORIZED_CATEGORY_ID,
  i18nKey: 'restaurantCatalog.uncategorized',
}

export const useRestaurantCatalogStore = defineStore('restaurantCatalog', {
  state: () => ({
    categories: [],
    selectedCategoryId: null,
    isCategoriesLoading: false,
    categoriesError: null,
    products: [],
    productsTotal: 0,
    productsSkip: 0,
    productsLimit: RESTAURANT_PRODUCTS_PAGE_SIZE,
    isProductsLoading: false,
    productsError: null,
    productsRequestId: 0,
    isProductUpdating: false,
  }),
  getters: {
    selectedCategory: (state) =>
      state.categories.find((category) => category.id === state.selectedCategoryId) ?? null,
    productsPageIndex: (state) =>
      state.productsLimit > 0 ? Math.floor(state.productsSkip / state.productsLimit) : 0,
    productsPageCount: (state) =>
      state.productsLimit > 0
        ? Math.max(1, Math.ceil(state.productsTotal / state.productsLimit))
        : 1,
  },
  actions: {
    clear() {
      this.categories = []
      this.selectedCategoryId = null
      this.isCategoriesLoading = false
      this.categoriesError = null
      this.products = []
      this.productsTotal = 0
      this.productsSkip = 0
      this.productsLimit = RESTAURANT_PRODUCTS_PAGE_SIZE
      this.isProductsLoading = false
      this.productsError = null
      this.productsRequestId = 0
      this.isProductUpdating = false
    },

    setSelectedCategoryId(categoryId) {
      const nextId = categoryId ? String(categoryId) : null
      if (nextId === this.selectedCategoryId) {
        return
      }
      this.selectedCategoryId = nextId
      this.productsSkip = 0
      return this.loadProducts()
    },

    setProductsPage(pageIndex) {
      const nextSkip = Math.max(0, Number(pageIndex) || 0) * this.productsLimit
      if (nextSkip === this.productsSkip) {
        return
      }
      this.productsSkip = nextSkip
      return this.loadProducts()
    },

    async loadCategories() {
      this.isCategoriesLoading = true
      this.categoriesError = null

      try {
        const list = await fetchRestaurantCategoriesApi()
        this.categories = [...list, UNCATEGORIZED_TAB]
        const stillSelected = this.categories.some(
          (category) => category.id === this.selectedCategoryId,
        )
        if (!stillSelected) {
          this.selectedCategoryId = this.categories[0]?.id ?? null
          this.productsSkip = 0
        }
        await this.loadProducts()
      } catch (error) {
        this.categoriesError = error
        this.categories = []
        this.selectedCategoryId = null
        this.products = []
        this.productsTotal = 0
      } finally {
        this.isCategoriesLoading = false
      }
    },

    async loadProducts() {
      const categoryId = this.selectedCategoryId
      if (!categoryId) {
        this.products = []
        this.productsTotal = 0
        this.productsError = null
        this.isProductsLoading = false
        return
      }

      const requestId = ++this.productsRequestId
      const skip = this.productsSkip
      const limit = this.productsLimit

      this.isProductsLoading = true
      this.productsError = null

      try {
        const { items, total } = await fetchRestaurantProductsApi({
          categoryId,
          skip,
          limit,
        })
        if (requestId !== this.productsRequestId) {
          return
        }
        this.products = items
        this.productsTotal = total
      } catch (error) {
        if (requestId !== this.productsRequestId) {
          return
        }
        this.productsError = error
        this.products = []
        this.productsTotal = 0
      } finally {
        if (requestId === this.productsRequestId) {
          this.isProductsLoading = false
        }
      }
    },

    async setProductAvailable(productId, isAvailable) {
      if (!productId || this.isProductUpdating) {
        return
      }
      this.isProductUpdating = true
      try {
        await updateRestaurantProductApi(productId, { is_available: Boolean(isAvailable) })
        await this.loadProducts()
      } finally {
        this.isProductUpdating = false
      }
    },

    async deleteProduct(productId) {
      if (!productId || this.isProductUpdating) {
        return
      }
      this.isProductUpdating = true
      try {
        await deleteRestaurantProductApi(productId)
        if (this.products.length <= 1 && this.productsSkip > 0) {
          this.productsSkip = Math.max(0, this.productsSkip - this.productsLimit)
        }
        await this.loadProducts()
      } finally {
        this.isProductUpdating = false
      }
    },
  },
})
